// ============================================
// FILE: /frontend/src/components/modals/WhatsAppSafetyModal.jsx
// PURPOSE: Safety warning modal before redirecting to WhatsApp
// ============================================

import { useState } from 'react';
import { AlertCircle, X } from 'lucide-react';
import { Button } from '../ui/button';

export default function WhatsAppSafetyModal({ isOpen, onClose, onProceed, sellerName }) {
  console.log('[WhatsAppSafety] Modal rendered', { isOpen, sellerName });

  const [acknowledged, setAcknowledged] = useState(false);

  // ========================================
  // HANDLE PROCEED TO WHATSAPP
  // ========================================
  const handleProceed = () => {
    if (!acknowledged) {
      console.log('[WhatsAppSafety] User tried to proceed without acknowledging');
      return;
    }

    console.log('[WhatsAppSafety] User acknowledged and proceeding to WhatsApp');
    onProceed();
    onClose();
    setAcknowledged(false); // Reset for next time
  };

  // ========================================
  // HANDLE MODAL CLOSE
  // ========================================
  const handleClose = () => {
    console.log('[WhatsAppSafety] Modal closed');
    onClose();
    setAcknowledged(false); // Reset for next time
  };

  // ========================================
  // HANDLE CHECKBOX CHANGE
  // ========================================
  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    console.log('[WhatsAppSafety] User acknowledgement:', checked);
    setAcknowledged(checked);
  };

  if (!isOpen) return null;

  // ========================================
  // RENDER MODAL
  // ========================================
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        
        {/* ========================================
            HEADER SECTION
            ======================================== */}
        <div className="sticky top-0 bg-amber-50 border-b border-amber-200 p-4 sm:p-6 flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 sm:gap-3 flex-1">
            <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-amber-900">
                Safety Warning
              </h2>
              <p className="text-xs sm:text-sm text-amber-800 mt-1">
                You are being redirected to a third-party platform
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-amber-600 hover:text-amber-800 transition-colors flex-shrink-0"
            aria-label="Close modal"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* ========================================
            CONTENT SECTION - SAFETY TIPS
            ======================================== */}
        <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
            <h3 className="font-semibold text-amber-900 text-sm sm:text-base mb-2 sm:mb-3">
              Protect Yourself
            </h3>
            <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-amber-800">
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold flex-shrink-0">•</span>
                <span>Exercise caution when dealing with any seller or buyer</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold flex-shrink-0">•</span>
                <span>Verify all information independently before making any commitments</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold flex-shrink-0">•</span>
                <span>Be aware of common scam tactics (advance payments, urgency pressure, too-good-to-be-true deals)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold flex-shrink-0">•</span>
                <span>Trust your judgment - if something seems suspicious, do not proceed</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold flex-shrink-0">•</span>
                <span>Consider meeting in public places and bringing someone with you</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold flex-shrink-0">•</span>
                <span>Seek professional advice (legal, financial) for significant transactions</span>
              </li>
            </ul>
          </div>

          {/* ========================================
              ACKNOWLEDGEMENT CHECKBOX
              ======================================== */}
          <div className="bg-white border border-amber-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-start gap-2 sm:gap-3">
              <input
                type="checkbox"
                id="safety-acknowledgement"
                checked={acknowledged}
                onChange={handleCheckboxChange}
                className="mt-0.5 sm:mt-1 cursor-pointer w-4 h-4"
              />
              <label 
                htmlFor="safety-acknowledgement" 
                className="text-xs sm:text-sm text-gray-700 cursor-pointer flex-1"
              >
                I have read and understood these safety warnings
              </label>
            </div>
          </div>
        </div>

        {/* ========================================
            FOOTER SECTION - ACTION BUTTONS
            ======================================== */}
        <div className="bg-gray-50 border-t p-4 sm:p-6 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            className="w-full sm:w-auto text-xs sm:text-sm h-9 sm:h-10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleProceed}
            disabled={!acknowledged}
            className="w-full sm:w-auto text-xs sm:text-sm h-9 sm:h-10"
          >
            Proceed to WhatsApp
          </Button>
        </div>
      </div>
    </div>
  );
}